import { useState } from "react";
import {
  AlertTriangle,
  Check,
  Copy,
  ExternalLink,
  Terminal,
} from "lucide-react";
import { Tonal } from "@/components/ui/tonal";
import { Kicker } from "@/components/ui/kicker";
import { Chip } from "@/components/ui/chip";
import { Button } from "@/components/ui/button";
import { formatDate, formatDuration } from "./workspace-formatters";

export interface WorkspaceFailureDiagnosticsProps {
  walkthrough: any;
  stepsCount: number;
}

export function WorkspaceFailureDiagnostics({
  walkthrough,
  stepsCount,
}: WorkspaceFailureDiagnosticsProps) {
  const [copiedCmd, setCopiedCmd] = useState(false);

  const errorMessage: string =
    walkthrough.errorMessage ??
    walkthrough.blocked?.reason ??
    "This walkthrough encountered an error and could not complete.";

  const isTimeout =
    errorMessage.toLowerCase().includes("timed out") ||
    errorMessage.toLowerCase().includes("no word from the agent");

  const phaseLabel = walkthrough.blocked?.reason
    ? "Precondition blocked"
    : walkthrough.liveState?.phase
      ? `Aborted in ${walkthrough.liveState.phase} phase`
      : stepsCount === 0
        ? "Aborted before step 1"
        : `Aborted after ${stepsCount} ${stepsCount === 1 ? "step" : "steps"}`;

  const runnerOrigin =
    walkthrough.runnerMode ??
    (walkthrough.clientOrigin === "local"
      ? "Local CLI runner"
      : "Cloud runner");
  const runnerDisplay = walkthrough.mode
    ? `${runnerOrigin} (${walkthrough.mode})`
    : runnerOrigin;

  const startedAtDisplay =
    formatDate(walkthrough.startedAt ?? walkthrough.createdAt) ?? "—";
  const abortedTimestamp =
    walkthrough.completedAt ?? walkthrough.liveState?.lastEventAt ?? null;
  const completedAtDisplay =
    formatDate(abortedTimestamp) ??
    (isTimeout ? "Timed out (no completion event)" : "Not recorded");

  const elapsedDisplay =
    formatDuration(walkthrough.startedAt, abortedTimestamp) ??
    (isTimeout ? "> 20m (worker timeout)" : "—");

  const build = walkthrough.build as
    | {
        label?: string;
        git_sha?: string;
        git_branch?: string;
        ci?: { provider?: string; run_url?: string };
      }
    | null
    | undefined;

  const buildParts = [
    build?.label,
    build?.git_branch,
    build?.git_sha ? build.git_sha.slice(0, 7) : undefined,
  ].filter(Boolean);
  const buildSummary =
    buildParts.length > 0
      ? buildParts.join(" · ")
      : walkthrough.buildId
        ? String(walkthrough.buildId).slice(0, 8)
        : null;

  const scenarioTarget =
    walkthrough.scenario?.slug ??
    walkthrough.scenario?.id ??
    walkthrough.scenarioId ??
    "<scenario-slug>";
  const cliCommand = `npx @iamu-dev/iamu run ${scenarioTarget}`;

  const handleCopyCommand = () => {
    navigator.clipboard?.writeText(cliCommand);
    setCopiedCmd(true);
    setTimeout(() => setCopiedCmd(false), 2000);
  };

  return (
    <Tonal
      padding="lg"
      className="space-y-4 border-destructive/40 bg-destructive/5"
    >
      {/* Header & Phase */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <AlertTriangle
            className="size-4 text-destructive shrink-0"
            aria-hidden="true"
          />
          <Kicker className="text-destructive font-semibold">
            Execution failure diagnostics
          </Kicker>
        </div>
        <Chip tone="critical">{phaseLabel}</Chip>
      </div>

      {/* Primary Error Message */}
      <div className="space-y-1.5">
        <p className="text-sm font-medium text-destructive">{errorMessage}</p>
        {walkthrough.blocked?.reason &&
          walkthrough.errorMessage &&
          walkthrough.blocked.reason !== walkthrough.errorMessage && (
            <p className="text-xs text-muted-foreground">
              <strong className="font-medium text-foreground">
                Precondition block:
              </strong>{" "}
              {walkthrough.blocked.reason}
            </p>
          )}
      </div>

      {/* Diagnostic Log Output */}
      <div className="rounded-md border border-border/80 bg-background/80 p-3 font-mono text-xs text-muted-foreground space-y-1">
        <div>
          <span className="text-foreground font-semibold">[status]</span> failed
          · {stepsCount} {stepsCount === 1 ? "step" : "steps"} recorded
        </div>
        {walkthrough.liveState?.phase && (
          <div>
            <span className="text-foreground font-semibold">[live_state]</span>{" "}
            phase={walkthrough.liveState.phase}
            {typeof walkthrough.liveState.lastSeq === "number"
              ? ` · seq=#${walkthrough.liveState.lastSeq}`
              : ""}
            {walkthrough.liveState.lastEventAt
              ? ` · last_heartbeat=${formatDate(walkthrough.liveState.lastEventAt) ?? walkthrough.liveState.lastEventAt}`
              : ""}
          </div>
        )}
        <div>
          <span className="text-foreground font-semibold">[reason]</span>{" "}
          {errorMessage}
        </div>
      </div>

      {/* Execution Provenance Grid */}
      <dl className="grid grid-cols-1 gap-3 border-t border-border/60 pt-3 text-xs sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <dt className="text-muted-foreground">Runner / Mode</dt>
          <dd className="mt-0.5 font-medium text-foreground">
            {runnerDisplay}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Target URL</dt>
          <dd
            className="mt-0.5 font-mono text-foreground truncate"
            title={walkthrough.targetUrl ?? undefined}
          >
            {walkthrough.targetUrl ?? "Not recorded"}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Elapsed before failure</dt>
          <dd className="mt-0.5 font-medium tabular-nums text-foreground">
            {elapsedDisplay}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Started</dt>
          <dd className="mt-0.5 font-medium tabular-nums text-foreground">
            {startedAtDisplay}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Aborted / Completed</dt>
          <dd className="mt-0.5 font-medium tabular-nums text-foreground">
            {completedAtDisplay}
          </dd>
        </div>
        {buildSummary && (
          <div>
            <dt className="text-muted-foreground">Build / Commit</dt>
            <dd className="mt-0.5 flex items-center gap-1.5 font-mono text-foreground">
              <span className="truncate">{buildSummary}</span>
              {build?.ci?.run_url && (
                <a
                  href={build.ci.run_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-0.5 text-primary hover:underline shrink-0"
                >
                  <span>{build.ci.provider || "CI"}</span>
                  <ExternalLink className="size-3" />
                </a>
              )}
            </dd>
          </div>
        )}
      </dl>

      {/* Local CLI Reproduction Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/80 bg-background px-3 py-2 text-xs">
        <div className="flex items-center gap-2 min-w-0">
          <Terminal
            className="size-3.5 text-muted-foreground shrink-0"
            aria-hidden="true"
          />
          <span className="text-muted-foreground shrink-0">
            Reproduce locally:
          </span>
          <code className="font-mono text-foreground truncate">
            {cliCommand}
          </code>
        </div>
        <Button
          variant="outline"
          size="xs"
          onClick={handleCopyCommand}
          className="gap-1.5 shrink-0"
        >
          {copiedCmd ? (
            <>
              <Check className="size-3 text-finding-helps" />
              <span>Copied</span>
            </>
          ) : (
            <>
              <Copy className="size-3 text-muted-foreground" />
              <span>Copy command</span>
            </>
          )}
        </Button>
      </div>
    </Tonal>
  );
}
